// The pager under the quote's line-items table (quote-lines-pagination).
// Presentation only: every figure it shows comes from lib/linePager.ts, and it
// changes nothing but which slice of the rows the table draws.

import { AlertTriangle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import {
  PAGE_SIZES, pageCount, pageNumbers, pageSizeLabel, rangeLabel, type PageSize,
} from "@/lib/linePager";
import { colors, semanticColors, radius, spacing, heights, typography, iconSize, iconStroke } from "@/styles/tokens";

const BTN: React.CSSProperties = {
  height: heights.control, minWidth: heights.control, padding: `0 ${spacing[8]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: semanticColors.surface, color: semanticColors.textPrimary,
  fontSize: typography.fontSize.sm, cursor: "pointer", whiteSpace: "nowrap",
  fontVariantNumeric: "tabular-nums",
};

export function LinePager({
  total, page, pageSize, onPage, onPageSize, otherAttention = 0, onGoToNext,
  noun = "Lines", sizes = PAGE_SIZES, testIdPrefix = "line-pager",
}: {
  total: number;
  page: number;
  pageSize: PageSize;
  onPage: (page: number) => void;
  onPageSize: (size: PageSize) => void;
  /** How many lines on OTHER pages are unpriced or in error. */
  otherAttention?: number;
  onGoToNext?: () => void;
  // The same pager pages the Quotes list (quotes-list-usability): its own noun,
  // its own sizes (no "All"), and its own test-id prefix so the two never collide.
  noun?: string;
  sizes?: readonly PageSize[];
  testIdPrefix?: string;
}) {
  const id = (suffix: string) => `${testIdPrefix}-${suffix}`;
  const count = pageCount(total, pageSize);
  const onFirst = page <= 0;
  const onLast = page >= count - 1;
  const btn = (disabled: boolean): React.CSSProperties => ({
    ...BTN, opacity: disabled ? 0.45 : 1, cursor: disabled ? "default" : "pointer",
  });
  const iconBtn = (disabled: boolean): React.CSSProperties => ({
    ...btn(disabled), width: heights.control, padding: 0,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
  });

  return (
    <div data-testid={testIdPrefix} style={{
      borderTop: `1px solid ${semanticColors.border}`, backgroundColor: semanticColors.surface,
      padding: `${spacing[8]}px ${spacing[12]}px`, display: "flex", flexDirection: "column", gap: spacing[8],
    }}>
      {otherAttention > 0 && (
        <div data-testid={id("attention")} role="status" style={{
          display: "flex", alignItems: "center", gap: spacing[8], flexWrap: "wrap",
          padding: `${spacing[6]}px ${spacing[10]}px`, borderRadius: radius.base,
          backgroundColor: semanticColors.warningBg, border: `1px solid ${semanticColors.warningBorder}`,
          color: colors.amber800, fontSize: typography.fontSize.sm,
        }}>
          <AlertTriangle size={iconSize.md} strokeWidth={iconStroke.regular} />
          <span data-testid={id("attention-text")}>
            {otherAttention} {otherAttention === 1 ? "line" : "lines"} on other pages need{otherAttention === 1 ? "s" : ""} attention
          </span>
          <button type="button" data-testid={id("attention-next")} onClick={onGoToNext}
                  style={{ ...BTN, height: heights.input, color: colors.amber800,
                           border: `1px solid ${semanticColors.warningBorder}` }}>
            Go to next
          </button>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: spacing[12], flexWrap: "wrap", justifyContent: "space-between" }}>
        <span data-testid={id("range")} className="tabular-nums"
              style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
          {rangeLabel(page, total, pageSize, noun)}
        </span>

        <nav aria-label="Line pages" style={{ display: "flex", alignItems: "center", gap: spacing[4], flexWrap: "wrap" }}>
          <button type="button" data-testid={id("first")} disabled={onFirst} style={iconBtn(onFirst)}
                  aria-label="First page" title="First page" onClick={() => onPage(0)}>
            <ChevronsLeft size={iconSize.md} strokeWidth={iconStroke.regular} aria-hidden="true" />
          </button>
          <button type="button" data-testid={id("prev")} disabled={onFirst} style={iconBtn(onFirst)}
                  aria-label="Previous page" title="Previous page" onClick={() => onPage(page - 1)}>
            <ChevronLeft size={iconSize.md} strokeWidth={iconStroke.regular} aria-hidden="true" />
          </button>
          {pageNumbers(page, count).map((p, i) => p === null ? (
            <span key={`gap-${i}`} aria-hidden="true" style={{ color: semanticColors.textMuted }}>…</span>
          ) : (
            <button key={p} type="button" data-testid={id(`page-${p + 1}`)}
                    aria-current={p === page ? "page" : undefined}
                    style={p === page
                      ? { ...BTN, backgroundColor: colors.indigo600, border: `1px solid ${colors.indigo600}`,
                          color: semanticColors.onAccent, fontWeight: typography.fontWeight.medium }
                      : BTN}
                    onClick={() => onPage(p)}>{p + 1}</button>
          ))}
          <button type="button" data-testid={id("next")} disabled={onLast} style={iconBtn(onLast)}
                  aria-label="Next page" title="Next page" onClick={() => onPage(page + 1)}>
            <ChevronRight size={iconSize.md} strokeWidth={iconStroke.regular} aria-hidden="true" />
          </button>
          <button type="button" data-testid={id("last")} disabled={onLast} style={iconBtn(onLast)}
                  aria-label="Last page" title="Last page" onClick={() => onPage(count - 1)}>
            <ChevronsRight size={iconSize.md} strokeWidth={iconStroke.regular} aria-hidden="true" />
          </button>
        </nav>

        <label style={{ display: "flex", alignItems: "center", gap: spacing[6],
                        fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
          {noun} per page
          <select data-testid={id("size")} value={pageSize}
                  onChange={(e) => onPageSize(Number(e.target.value) as PageSize)}
                  style={{ height: heights.input, border: `1px solid ${semanticColors.border}`,
                           borderRadius: radius.base, backgroundColor: semanticColors.surface,
                           fontSize: typography.fontSize.sm }}>
            {sizes.map((s) => <option key={s} value={s}>{pageSizeLabel(s)}</option>)}
          </select>
        </label>
      </div>
    </div>
  );
}
